/**
 * Typed API client for the Terramantle Worker.
 * All requests include the OIDC access token as a Bearer header.
 */
import { traceHeaders } from '@/lib/telemetry'
import type { OrgItem } from '@/lib/apiTypes'

// The registry/API worker origin. VITE_API_URL overrides for non-prod tenants.
// The default is the PRODUCTION registry host, NOT window.location.origin: the SPA
// (portal.terramantle.dev) and the API (registry.terramantle.dev) are different
// origins, so falling back to the page origin would make a clean/CI build (which
// has no VITE_API_URL, since the env files are gitignored) silently call the app
// host and every request 404. A hardcoded prod default fails safe.
export const API_BASE =
  (import.meta.env['VITE_API_URL'] as string) || 'https://registry.terramantle.dev'

let getAccessToken: (() => Promise<string>) | null = null

export function setTokenProvider(fn: () => Promise<string>): void {
  getAccessToken = fn
}

async function authHeader(): Promise<Record<string, string>> {
  if (!getAccessToken) return {}
  const token = await getAccessToken()
  return { Authorization: `Bearer ${token}` }
}

export async function authorizationHeader(): Promise<string> {
  if (!getAccessToken) return ''
  const token = await getAccessToken()
  return `Bearer ${token}`
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  isRetry = false,
  signal?: AbortSignal,
): Promise<T> {
  const headers: Record<string, string> = {
    ...(await authHeader()),
    ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    // THE single trace-context injection point for the whole SPA. §22 notes there is exactly one
    // request() builder, which is why propagation is one line here rather than a cross-cutting change.
    //
    // traceparent/tracestate make the Worker's server span a real CHILD of this fetch span - that is
    // what makes §21's structural query `{browser error} >> {worker error}` resolve. baggage carries
    // journey.id and journey.name and NOTHING else: never user.id, never tenant.id, never a token,
    // because the Worker treats baggage as attacker-controlled and derives identity from the
    // validated bearer token instead (§11).
    ...traceHeaders(),
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'unknown', message: 'Unknown error' }))
    const body_ = err as { error: string; message: string }

    // User record missing (e.g. local D1 was wiped) - re-register then retry once
    if (body_.message?.includes('not registered') && !isRetry) {
      await fetch(`${API_BASE}/api/auth/me`, {
        method: 'POST',
        headers: await authHeader(),
      })
      return request<T>(method, path, body, true, signal)
    }

    throw Object.assign(new Error(body_.message ?? body_.error), {
      status: res.status,
      body: body_,
    })
  }

  return res.json() as Promise<T>
}

export const api = {
  get: <T>(path: string, opts?: { signal?: AbortSignal }) => request<T>('GET', path, undefined, false, opts?.signal),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
  joinDemoOrg: () => request<{ org: { id: string; name: string; slug: string }; already_member?: boolean }>('POST', '/api/demo/join'),
  /** Create an org. During the beta any tier is free. The org comes back with no policies and
   *  the policy feature switched on; the baseline set is chosen on the Policies page. */
  createOrg: (body: { name: string; tier: string }) =>
    request<{ org: OrgItem }>('POST', '/api/orgs', body),
}
